import { action, makeObservable, observable, reaction, runInAction } from 'mobx';
import { z } from 'zod';
import { apiGetAvailableEventsRequest } from 'src/api_openapi/v2/bet-glider/apiGetAvailableEvents';
import { apiGetSessionRequest } from 'src/api_openapi/v2/bet-glider/apiGetSession';
import { Common } from 'src/domains/common/Common';
import { AppLayoutsState } from 'src/domains/layouts/state/AppLayouts.state';
import { BreakpointsState } from 'src/domains/layouts/state/breakpointsState/BreakpointsState';
import { BetGliderPopup } from 'src/domains/layouts/state/popupState/popupTypes';
import { AutoWeakMap } from 'src_common/common/mobx-utils/AutoWeakMap';
import { EventModel } from 'src_common/common/websocket2/models/EventModel';
import { StreamingState } from '../streamingState/StreamingState';

export class BetGliderState {
    @observable public url = '';
    @observable private hasTriedLoadingAvailableEvents = false;
    @observable private isLoadingAvailableEvents = false;
    @observable private mappedEvents: Map<string, string> = new Map();
    private readonly streamingState: StreamingState;
    public static get = AutoWeakMap.create((common: Common) => new BetGliderState(common));

    private constructor(private readonly common: Common) {
        makeObservable(this);
        this.streamingState = StreamingState.get(common);

        this.observeViewportChanges();
    }

    public openBetGlider = async (race: EventModel): Promise<void> => {
        const breakpointsState = BreakpointsState.get(this.common);
        const isMobile = breakpointsState.tablet.isBiggerOrEq === false;

        if (isMobile === false) {
            return;
        }

        const launchUrl = await this.getLaunchUrl(race);

        if (launchUrl === null) {
            return;
        }

        this.show(launchUrl);
    };

    public getLaunchUrl = async (race: EventModel): Promise<string | null> => {
        const { gatewayApiHost } = this.common.envVariables;
        const { currentJwt, userId } = this.common.session;

        if (userId === null) {
            return null;
        }

        if (gatewayApiHost === null || gatewayApiHost === '') {
            console.error('Missing gatewayApiHost for bet glider request');
            return null;
        }

        if (currentJwt === null || currentJwt === '') {
            console.error('Missing token for bet glider request');
            return null;
        }

        const eventId = race.id2.toOldId().toString();

        const [response, streamUrl] = await Promise.all([
            apiGetSessionRequest(
                gatewayApiHost,
                {
                    requestBody: {
                        accountId: userId,
                        eventId,
                    },
                },
                {
                    authorization: `Bearer ${currentJwt}`,
                }
            ),
            this.streamingState.getModel(race.id).getStreamURL(),
        ]);

        if (response.status !== 200) {
            console.error('Bet glider launchUrl request failed', response);
            return null;
        }

        if (streamUrl !== null) {
            const launchUrl = response.body.launchUrl;
            try {
                const url = new URL(launchUrl);
                url.searchParams.set('streamingUrl', streamUrl);
                return url.toString();
            } catch {
                const separator = launchUrl.includes('?') ? '&' : '?';
                return `${launchUrl}${separator}streamingUrl=${encodeURIComponent(streamUrl)}`;
            }
        }

        return response.body.launchUrl;
    };

    public isRaceMapped = (race: EventModel): boolean => {
        this.ensureAvailableEventsLoaded();

        const eventId = race.id2.toOldId().toString();
        return this.mappedEvents.has(eventId);
    };

    @action public close = (): void => {
        this.url = '';
        AppLayoutsState.get(this.common).popupState.hide();
    };

    @action public onIframeMessage = (message: string): void => {
        if (message === 'BACK_ARROW_CLICKED') {
            this.close();
        }
    };

    @action private show = (url: string): void => {
        this.url = url;
        AppLayoutsState.get(this.common).popupState.show(new BetGliderPopup());
    };

    private observeViewportChanges = (): void => {
        reaction(
            () => ({
                hasOpenBetGlider: this.url !== '',
                isMobile: BreakpointsState.get(this.common).tablet.isBiggerOrEq === false,
            }),
            ({ hasOpenBetGlider, isMobile }) => {
                if (hasOpenBetGlider && isMobile === false) {
                    this.close();
                }
            }
        );
    };

    private ensureAvailableEventsLoaded = (): void => {
        if (this.hasTriedLoadingAvailableEvents || this.isLoadingAvailableEvents) {
            return;
        }

        runInAction(() => {
            this.hasTriedLoadingAvailableEvents = true;
            this.isLoadingAvailableEvents = true;
        });

        void this.loadAvailableEvents();
    };

    private loadAvailableEvents = async (): Promise<void> => {
        const { gatewayApiHost } = this.common.envVariables;

        if (gatewayApiHost === null || gatewayApiHost === '') {
            console.error('Missing gatewayApiHost for bet glider available events request');
            runInAction(() => {
                this.isLoadingAvailableEvents = false;
            });
            return;
        }

        const authorization = this.common.session.currentJwt;

        try {
            const response = await apiGetAvailableEventsRequest(
                gatewayApiHost,
                {},
                {
                    authorization:
                        authorization !== null && authorization !== '' ? `Bearer ${authorization}` : undefined,
                }
            );

            if (response.status !== 200) {
                console.error('Bet glider available events request failed', response);
                return;
            }

            const parsedMapping = z.record(z.string()).safeParse(response.body.value);

            if (!parsedMapping.success) {
                console.error('Bet glider available events payload is invalid', {
                    errors: parsedMapping.error.errors,
                    data: response.body.value,
                });
                return;
            }

            runInAction(() => {
                this.mappedEvents.clear();

                for (const [eventId, mappedEventId] of Object.entries(parsedMapping.data)) {
                    this.mappedEvents.set(eventId, mappedEventId);
                }
            });
        } catch (error) {
            console.error('Bet glider available events request error', error);
        } finally {
            runInAction(() => {
                this.isLoadingAvailableEvents = false;
            });
        }
    };
}
